import React, { useState, useEffect, useCallback } from 'react';
import { Btn, Modal, RNLLoader, Select, Spinner } from '../UI';
import * as api from '../../api';
import { fmt, fmtDate } from './mpoUtils';
import MPOStatusPill, { MPO_PILL } from './MPOStatusPill';
import MPOForm from './MPOForm';
import MPODetailView from './MPODetailView';
import Pagination from '../requisition/Pagination';

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoCart    = () => <svg {...ic}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>;
const IcoPlus    = () => <svg {...ic}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoClock   = () => <svg {...ic}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcoSearch  = () => <svg {...ic}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IcoFile    = () => <svg {...ic}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const IcoEye     = () => <svg {...ic}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IcoEdit    = () => <svg {...ic}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z"/></svg>;
const IcoTrash   = () => <svg {...ic}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IcoX       = () => <svg {...ic}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

// ─── Create Manually modal ────────────────────────────────────────────────────
function CreateManuallyModal({ suppliers, projects, onConfirm, onClose }) {
  const [supplierId, setSupplierId] = useState('');
  const [projectId, setProjectId]   = useState('');
  return (
    <Modal title="Create Purchase Order (Manual)" onClose={onClose} width={560}>
      <p style={{ margin: '0 0 18px', fontSize: 12.5, color: '#64748b' }}>
        Select a supplier and project to open a new blank PO.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <Select
          label="SUPPLIER *"
          title="Select Supplier"
          value={supplierId}
          options={suppliers}
          onChange={setSupplierId}
        />
        <Select
          label="PROJECT *"
          title="Select Project"
          value={projectId}
          options={projects}
          onChange={setProjectId}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onConfirm(supplierId, projectId)} disabled={!supplierId || !projectId}
          style={{ background: '#16a34a', color: '#fff' }}>
          Create PO
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Delete confirmation ──────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onClose, busy }) {
  return (
    <Modal title="Confirm Delete" onClose={onClose} width={400}>
      <div style={{ fontSize: 14, color: '#1e293b', marginBottom: 20 }}>{message}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn variant="danger" onClick={onConfirm} disabled={busy}>
          {busy ? <Spinner size={14} /> : 'Delete'}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Workspace quick-action tile ──────────────────────────────────────────────
function WorkspaceTile({ icon, title, subtitle, onClick, accentColor }) {
  const [hovered, setHovered] = useState(false);
  const color = accentColor || '#1e40af';
  return (
    <div onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        border: `1.5px solid ${color}`,
        borderRadius: 10, padding: '16px', cursor: 'pointer',
        background: color,
        filter: hovered ? 'brightness(0.88)' : 'none',
        transition: 'filter 0.18s ease',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: '#fff' }}>
        {icon}
        <span style={{ fontWeight: 700, fontSize: 13.5, color: '#fff' }}>{title}</span>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{subtitle}</div>
    </div>
  );
}

// ─── Stat chip (Drafts view top bar) ─────────────────────────────────────────
function StatChip({ label, value, color }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: '#fff', border: '1px solid black', borderRadius: 8,
      padding: '8px 14px', minWidth: 100,
    }}>
      <span style={{ fontSize: 18, fontWeight: 800, color: color || '#1e293b', fontFamily: 'monospace' }}>{value}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{label}</span>
    </div>
  );
}

export default function MPOLanding({ initialProjectId, onGoToMyOrders }) {
  // ── Meta ──────────────────────────────────────────────────────────────────
  const [counts, setCounts]       = useState({ total: 0, draft: 0, pending: 0, returned: 0, rejected: 0, approved: 0, issued: 0 });
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects]   = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // ── View state ────────────────────────────────────────────────────────────
  const [view, setView]             = useState('workspace');
  const [listFilter, setListFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formInit, setFormInit]     = useState(null);
  const [editPO, setEditPO]         = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);

  // ── List state ────────────────────────────────────────────────────────────
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]     = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [error, setError]       = useState(null);
  const [confirm, setConfirm]   = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [page, setPage]         = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);

  // ── Load meta ─────────────────────────────────────────────────────────────
  const loadMeta = useCallback(() => {
    setLoadingMeta(true);
    Promise.all([
      api.listMPOs({ limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'draft', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'pending_approval', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'returned', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'rejected', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'approved', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ status: 'issued', limit: 1 }).catch(() => ({ total: 0 })),
      api.listProjects(0).catch(() => ({ projects: [] })),
      api.listSuppliers(false, 0, 1000).catch(() => []),
    ]).then(([all, draft, pending, returned, rejected, approved, issued, pr, sup]) => {
      setCounts({
        total: all?.total || 0, draft: draft?.total || 0,
        pending: pending?.total || 0, returned: returned?.total || 0,
        rejected: rejected?.total || 0, approved: approved?.total || 0,
        issued: issued?.total || 0,
      });
      setProjects((pr?.projects || pr || []).map(p => ({ value: p.project_id || p.id, label: `${p.project_code} — ${p.project_name || p.name}` })));
      setSuppliers((Array.isArray(sup) ? sup : sup?.suppliers || []).map(s => ({ value: s.supplier_id || s.id, label: s.name })));
    }).finally(() => setLoadingMeta(false));
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // ── Load list ─────────────────────────────────────────────────────────────
  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = {
      limit:  page.limit,
      offset: page.skip,
      ...(listFilter       && { status:      listFilter }),
      ...(initialProjectId && { project_id:  initialProjectId }),
      ...(projectFilter    && { project_id:  projectFilter }),
      ...(supplierFilter   && { supplier_id: supplierFilter }),
      ...(search           && { search }),
    };
    api.listMPOs(params)
      .then(res => {
        setRows(res?.items || []);
        setTotalCount(res?.total || 0);
      })
      .catch(e => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page, listFilter, initialProjectId, projectFilter, supplierFilter, search]);

  useEffect(() => {
    if (view === 'list') loadList();
  }, [view, loadList]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goList = (filter = '') => {
    setListFilter(filter);
    setProjectFilter('');
    setSupplierFilter('');
    setSearchInput('');
    setSearch('');
    setPage({ skip: 0, limit: 10 });
    setView('list');
  };
  const goWorkspace = () => { setView('workspace'); setListFilter(''); loadMeta(); };

  const openDetail = async (po) => {
    setNavigating(true);
    try {
      const full = await api.getMPO(po.po_id);
      setSelectedPO(full); setView('detail');
    } catch (e) { setError(e.message || 'Failed to load PO.'); }
    finally { setNavigating(false); }
  };

  const openEdit = async (po) => {
    setNavigating(true);
    try {
      const full = po.items ? po : await api.getMPO(po.po_id);
      setEditPO(full); setView('form');
    } catch (e) { setError(e.message || 'Failed to load PO for edit.'); }
    finally { setNavigating(false); }
  };

  const refreshDetail = async () => {
    if (selectedPO) {
      try { const full = await api.getMPO(selectedPO.po_id); setSelectedPO(full); } catch {}
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteMPO(confirm.po.po_id);
      setConfirm(null); loadList(); loadMeta();
    } catch (e) { setError(e.message || 'Delete failed.'); setConfirm(null); }
    finally { setDeleting(false); }
  };

  // search is now server-side; rows is already the filtered result
  const filteredRows = rows;

  // ── Draft stats (totals from server; other fields from current page) ─────
  const draftStats = React.useMemo(() => {
    if (listFilter !== 'draft') return null;
    const today = new Date().toISOString().split('T')[0];
    return {
      total:    totalCount,
      items:    rows.reduce((s, r) => s + (r.item_count || 0), 0),
      value:    rows.reduce((s, r) => s + (parseFloat(r.grand_total) || 0), 0),
      local:    rows.filter(r => r.currency === 'NGN').length,
      imported: rows.filter(r => r.currency !== 'NGN').length,
      today:    rows.filter(r => (r.created_at || '').startsWith(today)).length,
    };
  }, [rows, listFilter, totalCount]);

  // ── RNLLoader ─────────────────────────────────────────────────────────────
  if (navigating) return <RNLLoader title="Opening PO..." subtitle="Loading purchase order details" />;

  // ── Form view ─────────────────────────────────────────────────────────────
  if (view === 'form') {
    return (
      <MPOForm
        existing={editPO}
        initSupplier={formInit?.supplier_id}
        initProject={formInit?.project_id}
        onSaved={() => { setEditPO(null); setFormInit(null); onGoToMyOrders ? onGoToMyOrders() : goWorkspace(); }}
        onCancel={() => { setEditPO(null); setFormInit(null); setView(editPO ? 'list' : 'workspace'); }}
      />
    );
  }

  // ── Detail view ───────────────────────────────────────────────────────────
  if (view === 'detail' && selectedPO) {
    return (
      <MPODetailView
        po={selectedPO}
        onBack={() => { setSelectedPO(null); setView('list'); }}
        onRefresh={refreshDetail}
        onEdit={(po) => openEdit(po)}
      />
    );
  }

  // ── Persistent action bar ─────────────────────────────────────────────────
  const isDraftActive   = view === 'list' && listFilter === 'draft';
  const isPendingActive = view === 'list' && listFilter === 'pending_approval';
  const isSearchActive  = view === 'list' && listFilter === '';

  const ActionBar = () => (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fff', border: '1px solid black', borderRadius: 10,
        padding: '6px 10px',
      }}>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <BarBtn icon={<IcoX />} label="Clear Page" onClick={goWorkspace} active={view === 'workspace'} />
          <Sep />
          <BarBtn icon={<IcoSearch />} label="Search" count={counts.total} onClick={() => goList('')} active={isSearchActive} />
          <BarBtn icon={<IcoFile />} label="Drafts" count={counts.draft} onClick={() => goList('draft')} active={isDraftActive} amber />
          <BarBtn icon={<IcoClock />} label="Pending" count={counts.pending} onClick={() => goList('pending_approval')} active={isPendingActive} orange />
          <Sep />
          <BarBtn icon={<IcoCart />} label="Load Purchase Request" onClick={() => {}} />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 18px', background: '#16a34a', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}
        >
          <IcoPlus /> Create Manually
        </button>
      </div>
    </div>
  );

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  if (view === 'list') {
    const isDraft   = listFilter === 'draft';
    const isPending = listFilter === 'pending_approval';
    const listLabel = isDraft ? 'Draft Purchase Orders' : isPending ? 'Pending POs'
      : listFilter ? `${MPO_PILL[listFilter]?.label || listFilter} Purchase Orders`.replace(/\b(\w)(\w*)/g, (_, f, r) => f + r.toLowerCase())
      : 'All Purchase Orders';
    const listDesc  = isPending ? 'Purchase Orders raised but not yet issued.'
      : listFilter === 'returned' ? 'Purchase Orders sent back to their requester for correction.'
      : listFilter === 'rejected' ? 'Purchase Orders an approver rejected.' : '';

    const thS = { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '10px 12px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textAlign: 'left' };
    const tdS = { padding: '9px 12px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
    const mono = { fontFamily: 'monospace', fontWeight: 600 };

    return (
      <div>
        <ActionBar />

        {/* Page title */}
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ margin: '0 0 2px', fontWeight: 800, fontSize: 20, color: 'var(--text-primary)' }}>Purchase Order Landing</h2>
          <p style={{ margin: 0, fontSize: 12.5, color: '#64748b' }}>Operational Purchase Order transaction screen — load approved requests, create POs, manage charges &amp; documents, save / submit / issue.</p>
        </div>

        {/* Breadcrumb */}
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
          <span style={{ color: '#0b318b', cursor: 'pointer' }} onClick={goWorkspace}>Purchase Order Landing</span>
          {' › '}
          <span style={{ color: '#1e293b' }}>{listLabel}</span>
        </div>
        {listDesc && <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#64748b' }}>{listDesc}</p>}

        {/* Draft stats bar */}
        {isDraft && draftStats && !loading && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatChip label="Total Draft POs"    value={draftStats.total}                  color="#1e40af" />
            <StatChip label="Total Line Items"   value={draftStats.items}                  color="#0891b2" />
            <StatChip label="Total Draft Value"  value={`₦${(draftStats.value/1e6).toFixed(2)}M`} color="#15803d" />
            <StatChip label="Local POs"          value={draftStats.local}                  color="#6d28d9" />
            <StatChip label="Import POs"         value={draftStats.imported}               color="#b45309" />
            <StatChip label="Created Today"      value={draftStats.today}                  color="#dc2626" />
          </div>
        )}

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          {/* Search bar */}
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}><IcoSearch /></span>
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage({ skip: 0, limit: 10 }); } }}
              placeholder={`Search PO No. / Supplier / Project${isDraft ? ' / Description' : ''}`}
              style={{ width: '100%', height: 36, boxSizing: 'border-box', padding: '0 10px 0 34px', fontSize: 12.5, border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none' }}
            />
          </div>
          <button
            onClick={() => { setSearch(searchInput.trim()); setPage({ skip: 0, limit: 10 }); }}
            style={{ height: 36, padding: '0 16px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Search
          </button>
          {/* Project filter */}
          <div style={{ minWidth: 200 }}>
            <Select
              label=""
              title="Select Project"
              value={projectFilter}
              options={projects}
              onChange={val => { setProjectFilter(val); setPage({ skip: 0, limit: 10 }); }}
              placeholder="All Projects"
              clearable
            />
          </div>
          {/* Supplier filter */}
          <div style={{ minWidth: 200 }}>
            <Select
              label=""
              title="Select Supplier"
              value={supplierFilter}
              options={suppliers}
              onChange={val => { setSupplierFilter(val); setPage({ skip: 0, limit: 10 }); }}
              placeholder="All Suppliers"
              clearable
            />
          </div>
          {(search || searchInput || projectFilter || supplierFilter) && (
            <button onClick={() => { setSearchInput(''); setSearch(''); setProjectFilter(''); setSupplierFilter(''); setPage({ skip: 0, limit: 10 }); }}
              style={{ padding: '7px 12px', fontSize: 12, border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: 'none', color: '#64748b' }}>
              Clear All
            </button>
          )}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Table */}
        <div style={{ background: '#fff', border: '1px solid black', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th style={{ ...thS, width: 34, textAlign: 'center' }}>S/N</th>
                  <th style={thS}>PO No.</th>
                  <th style={thS}>PO Date</th>
                  <th style={thS}>Supplier</th>
                  <th style={thS}>Project</th>
                  <th style={thS}>Status</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Grand Total</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Items</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center' }}><Spinner size={24} /></td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: '32px 20px', textAlign: 'center', color: '#9ca3af' }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
                    <div style={{ fontWeight: 600 }}>No purchase orders found</div>
                  </td></tr>
                )}
                {filteredRows.map((row, idx) => (
                  <tr key={row.po_id} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...tdS, textAlign: 'center', color: '#94a3b8' }}>{page.skip + idx + 1}</td>
                    <td style={tdS}>
                      <span style={{ ...mono, color: '#1e40af', cursor: 'pointer' }} onClick={() => openDetail(row)}>
                        {row.po_number || '—'}
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', fontFamily: 'var(--font)' }}>Serial {row.serial_number}</div>
                      </span>
                    </td>
                    <td style={{ ...tdS, color: '#64748b' }}>{fmtDate(row.po_date)}</td>
                    <td style={{ ...tdS, color: '#475569' }}>{row.supplier_name || '—'}</td>
                    <td style={tdS}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.project_name || '—'}</div>
                      {row.project_code && <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.project_code}</div>}
                    </td>
                    <td style={tdS}>
                      <MPOStatusPill status={row.status} />
                    </td>
                    <td style={{ ...tdS, ...mono, textAlign: 'right', color: '#1e40af', fontWeight: 700 }}>{fmt(row.grand_total)}</td>
                    <td style={{ ...tdS, textAlign: 'center', color: '#64748b' }}>{row.item_count}</td>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => openDetail(row)} title="View" style={{ background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}><IcoEye /></button>
                        {row.status === 'draft' && (
                          <>
                            <button onClick={() => openEdit(row)} title="Edit" style={{ background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#1e40af', display: 'flex', alignItems: 'center' }}><IcoEdit /></button>
                            <button onClick={() => setConfirm({ po: row })} title="Delete" style={{ background: 'none', border: '1.5px solid #fca5a5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}><IcoTrash /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && !loading && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
              {`${totalCount} purchase order${totalCount !== 1 ? 's' : ''}`}
            </div>
          )}
        </div>

        {totalCount > 10 && (
          <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
        )}

        {confirm && (
          <ConfirmModal message={`Delete PO ${confirm.po.po_number || confirm.po.serial_number}? This cannot be undone.`}
            onConfirm={handleDelete} onClose={() => setConfirm(null)} busy={deleting} />
        )}

        {showCreateModal && (
          <CreateManuallyModal suppliers={suppliers} projects={projects}
            onClose={() => setShowCreateModal(false)}
            onConfirm={(sid, pid) => { setShowCreateModal(false); setFormInit({ supplier_id: sid, project_id: pid }); setEditPO(null); setView('form'); }} />
        )}
      </div>
    );
  }

  // ── WORKSPACE VIEW ────────────────────────────────────────────────────────
  return (
    <div>
      <ActionBar />

      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 20, color: 'var(--text-primary)' }}>
          Purchase Order Landing
        </h2>
        <p style={{ margin: 0, fontSize: 12.5, color: '#64748b' }}>
          Operational Purchase Order transaction screen — load approved requests, create POs, manage charges &amp; documents, save / submit / issue.
        </p>
      </div>

      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
        Purchase Order Landing
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'stretch' }}>
        {/* Left: Workspace */}
        <div style={{ background: '#fff', border: '1px solid black', borderRadius: 12, padding: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b', marginBottom: 6 }}>Purchase Order Workspace</div>
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
            Load approved procurement lines into a same-supplier Purchase Order, or create one manually.
            Save as Draft → Submit → Approve → Issue → GRN.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <WorkspaceTile icon={<IcoCart />}   title="Load Purchase Request" subtitle="Load approved request lines"                        onClick={() => {}}                    accentColor="#1e40af" />
            <WorkspaceTile icon={<IcoPlus />}   title="Create Manually"       subtitle="New blank PO"                                        onClick={() => setShowCreateModal(true)} accentColor="#16a34a" />
            <WorkspaceTile icon={<IcoClock />}  title="Pending POs"           subtitle={`${counts.draft + counts.pending} draft / pending`}  onClick={() => goList('draft')}        accentColor="#d97706" />
            <WorkspaceTile icon={<IcoSearch />} title="Search POs"            subtitle={`${counts.total} processed`}                         onClick={() => goList('')}             accentColor="#7c3aed" />
          </div>
        </div>

        {/* Right: Status overview */}
        <div style={{ background: '#fff', border: '1px solid black', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b', marginBottom: 14 }}>Status Overview</div>
          {loadingMeta
            ? <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><Spinner size={20} /></div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { label: 'Draft',            val: counts.draft,    onClick: () => goList('draft'),            color: MPO_PILL.draft.col },
                  { label: 'Pending Approval', val: counts.pending,  onClick: () => goList('pending_approval'), color: MPO_PILL.pending_approval.col },
                  { label: 'Returned',         val: counts.returned, onClick: () => goList('returned'),         color: MPO_PILL.returned.col },
                  { label: 'Rejected',         val: counts.rejected, onClick: () => goList('rejected'),         color: MPO_PILL.rejected.col },
                  { label: 'Approved',         val: counts.approved, onClick: () => goList('approved'),         color: MPO_PILL.approved.col },
                  { label: 'Issued',           val: counts.issued,   onClick: () => goList('issued'),           color: MPO_PILL.issued.col },
                  { label: 'Total POs',        val: counts.total,    onClick: () => goList(''),                 color: '#1e293b', bold: true },
                ].map(({ label, val, onClick, color, bold }) => (
                  <div key={label} onClick={onClick} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  }}>
                    <span style={{ fontSize: 13, color, fontWeight: bold ? 700 : 500 }}>{label}</span>
                    <span style={{ fontSize: 14, fontWeight: bold ? 800 : 700, color, fontFamily: 'monospace' }}>{val}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      {showCreateModal && (
        <CreateManuallyModal suppliers={suppliers} projects={projects}
          onClose={() => setShowCreateModal(false)}
          onConfirm={(sid, pid) => { setShowCreateModal(false); setFormInit({ supplier_id: sid, project_id: pid }); setEditPO(null); setView('form'); }} />
      )}
    </div>
  );
}

// ─── Bar button ───────────────────────────────────────────────────────────────
function BarBtn({ icon, label, count, onClick, active, amber, orange }) {
  const bg = active ? (amber ? '#fef3c7' : '#eff6ff') : 'none';
  const border = active ? (amber ? '1.5px solid #fbbf24' : '1.5px solid #bfdbfe') : '1.5px solid transparent';
  const color  = active ? (amber ? '#b45309' : '#1e40af') : '#374151';
  const badgeBg = amber ? '#fef3c7' : orange ? '#ffedd5' : '#e0e7ff';
  const badgeColor = amber ? '#b45309' : orange ? '#c2410c' : '#1e40af';
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px',
      background: bg, border, borderRadius: 7, cursor: 'pointer', color,
      fontSize: 12.5, fontWeight: 600, transition: 'all 0.15s',
    }}>
      {icon} {label}
      {count !== undefined && (
        <span style={{ background: badgeBg, color: badgeColor, borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, marginLeft: 2 }}>
          {count}
        </span>
      )}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, height: 18, background: '#e2e8f0', margin: '0 2px' }} />;
}
